import { useEffect, useMemo, useState } from "react";
import PublicShell from "@/components/PublicShell";
import { VideoSlot } from "@/components/VideoSlot";
import ArtCard from "@/components/ArtCard";
import { api } from "@/lib/api";
import { useCopy } from "@/context/ContentContext";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const title = useCopy("gallery_title", "The Collection");
  const intro = useCopy("gallery_intro", "Each piece is one of a kind. Explore the current body of work.");

  useEffect(() => {
    api.get("/artworks").then(r => setItems(r.data));
    api.get("/categories").then(r => setCats(r.data));
  }, []);

  // Only show categories that have at least one artwork; no "All" tab.
  const options = useMemo(
    () => cats.map(c => c.name).filter(name => items.some(a => a.category === name)),
    [cats, items]
  );

  useEffect(() => {
    if (!options.length) return;
    if (!active || !options.includes(active)) setActive(options[0]);
  }, [options, active]);

  const filtered = useMemo(() => items.filter(a => a.category === active), [items, active]);

  return (
    <PublicShell>
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <VideoSlot page="gallery" spot="top" className="mb-16" />
        <div className="text-center mb-14">
          <div className="overline text-mute mb-4">Collection</div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light text-ink mb-6">{title}</h1>
          <p className="max-w-2xl mx-auto text-ink-soft leading-relaxed">{intro}</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center mb-16 border-t border-b border-ink-10 py-6">
          {options.map(o => (
            <button
              key={o}
              onClick={() => setActive(o)}
              data-testid={`filter-${o.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] transition-colors ${active === o ? "bg-ink text-[color:var(--brand-cream)]" : "text-ink hover:text-accent"}`}>
              {o}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <p className="text-mute text-center">No artworks in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filtered.map(a => <ArtCard key={a.id} art={a} />)}
          </div>
        )}
        <VideoSlot page="gallery" spot="bottom" className="mt-20" />
      </section>
    </PublicShell>
  );
}
